import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, HandHeart, ShieldCheck, Lock, TrendingUp } from "lucide-react";
import { Input } from "@/components/ui/input";
import { PageHero, Overline } from "@/components/Bits";
import { SecuritySection } from "@/components/SecuritySection";
import { Reveal } from "@/components/Reveal";
import { PRESET_AMOUNTS } from "@/data/content";
import { startDonation, getDonationStats } from "@/lib/api";
import { useValidationPopup } from "@/components/ValidationPopup";
import { isValidEmail } from "@/lib/format";

const IMPACT = [
  ["$25", "Put Resources in Reach", "Can help provide books, learning materials, sports supplies, or other essentials needed to participate and succeed."],
  ["$50", "Help Someone Participate", "Can help offset the cost of educational activities, youth athletics, wellness programs, workshops, or other opportunities that may otherwise be out of reach."],
  ["$100", "Invest in Growth", "Can help provide equipment, program materials, training resources, or access to experiences that support learning, development, and well-being."],
  ["$250", "Bring an Opportunity to Life", "Can help support a workshop, clinic, mentorship activity, educational experience, or community-based program serving multiple participants."],
  ["$500", "Strengthen a Program", "Can help fund the people, materials, equipment, and resources needed to deliver meaningful programming in the communities we serve."],
  ["$1,000", "Create Greater Access", "Can help expand a program or initiative, provide assistance to multiple participants, or support a community partnership that creates opportunities for more people."],
];

function ImpactMeter() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    getDonationStats().then(setStats).catch(() => {});
  }, []);
  if (!stats) return null;
  const pct = Math.min(100, stats.goal ? (stats.raised / stats.goal) * 100 : 0);
  const money = (n) => `$${Math.round(n).toLocaleString()}`;
  return (
    <Reveal>
      <div className="mb-10 rounded-3xl border border-gold/25 bg-gold/[0.05] p-6 md:p-8" data-testid="impact-meter">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 place-items-center rounded-full bg-gold/15 text-gold"><TrendingUp className="h-5 w-5" /></span>
            <div>
              <p className="font-heading text-lg font-bold tracking-tight" data-testid="impact-meter-raised">
                {money(stats.raised)} raised in {stats.month}
              </p>
              <p className="text-sm text-muted-foreground">
                {stats.gifts} {stats.gifts === 1 ? "gift" : "gifts"} this month · goal {money(stats.goal)}
              </p>
            </div>
          </div>
          <span className="font-heading text-2xl font-bold text-gold">{Math.round(pct)}%</span>
        </div>
        <div className="mt-5 h-3 w-full overflow-hidden rounded-full bg-secondary">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="h-full rounded-full bg-gold"
            data-testid="impact-meter-bar"
          />
        </div>
        <p className="mt-4 text-sm font-medium text-foreground">
          {pct >= 100 ? "We hit this month's goal — every extra gift goes even further." : "Your gift moves the needle. Be part of this month's momentum."}
        </p>
      </div>
    </Reveal>
  );
}

export default function Donate() {
  const [params] = useSearchParams();
  const { notify, popup } = useValidationPopup();
  const [amount, setAmount] = useState(100);
  const [custom, setCustom] = useState("");
  const [frequency, setFrequency] = useState(params.get("frequency") === "monthly" ? "monthly" : "one_time");
  const [donor, setDonor] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);

  const effective = custom ? parseFloat(custom) : amount;

  const onDonate = async () => {
    if (!effective || effective < 5) {
      notify("Please enter a donation amount of $5 or more to continue.");
      return;
    }
    if (donor.email.trim() && !isValidEmail(donor.email)) {
      notify("Please enter a valid email address — make sure it includes an \u201c@\u201d (e.g. name@email.com).");
      return;
    }
    setLoading(true);
    try {
      const { checkout_url } = await startDonation({
        amount: effective,
        frequency,
        donor_name: donor.name,
        donor_email: donor.email,
        origin_url: window.location.origin,
      });
      window.location.href = checkout_url;
    } catch (e) {
      notify(e?.response?.data?.detail || "We couldn't start checkout just now. Please try again in a moment.");
      setLoading(false);
    }
  };

  return (
    <>
      {popup}
      <PageHero
        label="Donate"
        title="Your gift builds"
        accent="brighter futures."
        subtitle="Every contribution directly funds education, athletics, health, leadership, and innovation. Give once or monthly — securely, through Stripe."
      />

      <section className="mx-auto max-w-6xl px-5 py-20 md:px-8 md:py-24">
        <ImpactMeter />
        <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr]">
          {/* Donation card */}
          <Reveal>
            <div className="rounded-3xl border border-border bg-card p-7 md:p-10" data-testid="donation-widget">
              <div className="flex rounded-full bg-secondary p-1.5">
                {[
                  ["one_time", "One-time"],
                  ["monthly", "Monthly"],
                ].map(([val, label]) => (
                  <button
                    key={val}
                    onClick={() => setFrequency(val)}
                    data-testid={`freq-${val}`}
                    className={`flex-1 rounded-full py-2.5 text-sm font-semibold transition-colors ${
                      frequency === val ? "bg-gold text-navy-deep" : "text-muted-foreground"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <p className="mt-8 overline text-muted-foreground">Choose an amount</p>
              <div className="mt-4 grid grid-cols-3 gap-3">
                {PRESET_AMOUNTS.map((a) => (
                  <button
                    key={a}
                    onClick={() => {
                      setAmount(a);
                      setCustom("");
                    }}
                    data-testid={`amount-${a}`}
                    className={`rounded-xl border py-4 font-heading text-lg font-bold transition-colors ${
                      !custom && amount === a
                        ? "border-gold bg-gold/10 text-gold"
                        : "border-border hover:border-gold"
                    }`}
                  >
                    ${a}
                  </button>
                ))}
              </div>

              <div className="mt-4">
                <div className="flex items-center gap-2 rounded-xl border border-border bg-background px-4 focus-within:border-gold">
                  <span className="font-heading text-lg font-bold text-muted-foreground">$</span>
                  <Input
                    type="number"
                    min="5"
                    value={custom}
                    onChange={(e) => setCustom(e.target.value)}
                    placeholder="Custom amount"
                    data-testid="amount-custom"
                    className="h-14 border-0 bg-transparent px-0 text-lg focus-visible:ring-0"
                  />
                  {frequency === "monthly" && (
                    <span className="text-sm text-muted-foreground">/mo</span>
                  )}
                </div>
              </div>

              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                <Input
                  className="h-12 rounded-xl border-input bg-background px-4 text-base md:text-sm focus-visible:ring-2 focus-visible:ring-gold"
                  placeholder="Name (optional)"
                  data-testid="donor-name"
                  value={donor.name}
                  onChange={(e) => setDonor({ ...donor, name: e.target.value })}
                />
                <Input
                  type="email"
                  className="h-12 rounded-xl border-input bg-background px-4 text-base md:text-sm focus-visible:ring-2 focus-visible:ring-gold"
                  placeholder="Email (for receipt)"
                  data-testid="donor-email"
                  value={donor.email}
                  onChange={(e) => setDonor({ ...donor, email: e.target.value })}
                />
              </div>

              <button
                onClick={onDonate}
                disabled={loading}
                data-testid="donate-submit"
                className="mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-gold py-4 text-base font-semibold text-navy-deep transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <HandHeart className="h-5 w-5" />}
                {loading
                  ? "Redirecting to secure checkout…"
                  : `Donate ${effective ? `$${effective}` : ""}${frequency === "monthly" ? "/mo" : ""}`}
              </button>

              <div className="mt-5 flex items-center justify-center gap-5 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5"><Lock className="h-3.5 w-3.5 text-gold" /> Encrypted</span>
                <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-gold" /> Secured by Stripe</span>
              </div>
              <p className="mt-3 text-center text-[0.7rem] leading-relaxed text-muted-foreground" data-testid="donate-legal-agreement">
                By donating, you agree to our{" "}
                <a href="/terms" target="_blank" rel="noreferrer" className="text-gold underline underline-offset-2 hover:opacity-80">Terms of Use</a>{" "}and{" "}
                <a href="/privacy" target="_blank" rel="noreferrer" className="text-gold underline underline-offset-2 hover:opacity-80">Privacy Policy</a>.
              </p>
            </div>
          </Reveal>

          {/* Impact list */}
          <Reveal delay={0.1}>
            <div>
              <Overline>Your Impact</Overline>
              <h2 className="mt-5 font-heading text-2xl font-bold tracking-tight sm:text-3xl">
                See what your gift can help make possible.
              </h2>
              <div className="mt-8 space-y-4">
                {IMPACT.map(([amt, title, text]) => (
                  <div key={amt} className="flair-card flex gap-4 rounded-2xl border border-border bg-card p-5" data-testid={`impact-tier-${amt.replace(/[^0-9]/g, "")}`}>
                    <span className="flair-icon shrink-0 font-heading text-xl font-bold text-gold">{amt}</span>
                    <div className="min-w-0">
                      <p className="font-heading text-sm font-bold tracking-tight text-foreground">{title}</p>
                      <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{text}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8 rounded-2xl border border-gold/25 bg-gold/[0.05] p-5" data-testid="impact-note">
                <p className="font-heading text-sm font-bold tracking-tight text-foreground">Every gift has a purpose.</p>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                  These examples illustrate how contributions may support our work. Actual needs and
                  program costs vary, allowing Thefrent Foundation to direct unrestricted gifts where
                  they can have the greatest impact.
                </p>
              </div>
              <p className="mt-6 text-xs leading-relaxed text-muted-foreground">
                Thefrent Foundation Inc. is a nonprofit organization. A receipt is emailed after
                your donation.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <SecuritySection />
    </>
  );
}
